import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { api } from '../../services/api';

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Adgangskoden skal være mindst 8 tegn');
      return;
    }

    if (password !== confirmPassword) {
      setError('Adgangskoderne matcher ikke');
      return;
    }

    if (!token) {
      setError('Ugyldigt link. Kontakt din administrator.');
      return;
    }

    setIsLoading(true);
    try {
      await api.setPasswordWithToken(token, password);
      setSuccess(true);
    } catch {
      setError('Linket er ugyldigt eller udløbet. Bestil et nyt via "Glemt adgangskode?" på login-siden.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 360, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <LocalShippingIcon color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                DORA Insight
              </Typography>
            </Box>
            <Alert severity="error">
              Ugyldigt link. Kontakt din administrator for en ny invitation.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 360, width: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <LocalShippingIcon color="primary" sx={{ fontSize: 36 }} />
              <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                DORA Insight
              </Typography>
            </Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Din adgangskode er gemt.
            </Alert>
            <Button
              component={Link}
              to="/login"
              fullWidth
              variant="contained"
            >
              Gå til login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 360, width: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LocalShippingIcon color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
              DORA Insight
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Vælg adgangskode
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Adgangskode"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="dense"
              required
              autoFocus
              autoComplete="new-password"
              helperText="Mindst 8 tegn"
            />
            <TextField
              fullWidth
              label="Bekræft adgangskode"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="dense"
              required
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Gem adgangskode'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
